import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Dropdown} from 'primereact/dropdown'
import React from 'react'
import TranslateUtils from '../../i18n/TranslateUtils'
import {useDatatableContext} from './DataTableContext'
import './styles.css'

const options = [
    {label: '10', value: 10},
    {label: '20', value: 20},
    {label: '30', value: 30},
    {label: '40', value: 40},
    {label: '50', value: 50},
    {label: '100', value: 100},
]

const StyledTablePaginator = React.memo(() => {
    useSignals()

    const {translateDatatable} = TranslateUtils()

    const {queryData, onChangePageNumber, onChangePageRows, onPageRowsHovered} =
        useDatatableContext()

    const numberOfElements = queryData?.numberOfElements || 0
    const currentPage = queryData?.number || 0
    const totalPages = queryData?.totalPages || 1
    const totalRecords = queryData?.totalElements || 0
    const currentPageSize = queryData?.size || 10
    const isFirstPage = queryData?.first
    const isLastPage = queryData?.last

    const pageSize = useSignal(10)

    const onPageChange = newPageNumber => {
        if (onChangePageNumber) {
            onChangePageNumber(newPageNumber)
        }
    }

    const onPageSizeChange = newPageRows => {
        pageSize.value = newPageRows

        if (onChangePageRows) {
            onChangePageRows(newPageRows)
        }
    }

    return (
        <div
            className="flex justify-content-between align-items-center px-3 border-round-bottom-xl"
            style={{
                backgroundColor: '#f8f9fa',
                height: '50px',
            }}>
            <div>
                <p className="font-bold">
                    {translateDatatable('Showing')}{' '}
                    {currentPage * currentPageSize + numberOfElements}{' '}
                    {translateDatatable('of')} {totalRecords}{' '}
                    {translateDatatable('total record(s)')}
                </p>
            </div>
            <div className="flex gap-1">
                <Button
                    icon="pi pi-angle-double-left  text-darkcyan"
                    className={`p-button-text p-button-plain ${
                        currentPage < 1 ? 'cursor-not-allowed' : ''
                    }`}
                    onClick={() => onPageChange(0)}
                    disabled={currentPage < 1}
                />
                <Button
                    icon="pi pi-angle-left  text-darkcyan"
                    className={`p-button-text p-button-plain ${
                        currentPage < 1 ? 'cursor-not-allowed' : ''
                    }`}
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage < 1}
                />

                {Array.from(Array(totalPages).keys()).map((item, index) => {
                    // Render all pages if totalPages <= 10
                    if (totalPages <= 6) {
                        return (
                            <Button
                                key={item}
                                label={item + 1}
                                className={`paginator-item ${
                                    currentPage === item
                                        ? 'font-bold active'
                                        : ''
                                }`}
                                onClick={() => onPageChange(item)}
                            />
                        )
                    } else {
                        // If totalPages > 10, render the first 3, the last 3, and currentPage +/- 2
                        if (
                            item < 3 ||
                            item >= totalPages - 3 ||
                            (item >= currentPage - 2 && item <= currentPage + 2)
                        ) {
                            return (
                                <Button
                                    key={item}
                                    label={item + 1}
                                    className={`paginator-item ${
                                        currentPage === item
                                            ? 'font-bold active'
                                            : ''
                                    }`}
                                    onClick={() => onPageChange(item)}
                                />
                            )
                        } else if (
                            (item === 3 && currentPage > 5) ||
                            (item === totalPages - 4 &&
                                currentPage < totalPages - 6)
                        ) {
                            // Render dots for skipped pages
                            return (
                                <span
                                    key={item}
                                    className="paginator-item align-self-center">
                                    ...
                                </span>
                            )
                        } else {
                            // Return null to avoid rendering unnecessary buttons
                            return null
                        }
                    }
                })}

                <Button
                    icon="pi pi-angle-right text-darkcyan"
                    className={`p-button-text p-button-plain ${
                        currentPage === totalPages - 1 || totalRecords === 0
                            ? 'cursor-not-allowed'
                            : ''
                    }`}
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={
                        currentPage === totalPages - 1 || totalRecords === 0
                    }
                />
                <Button
                    icon="pi pi-angle-double-right text-darkcyan"
                    className={`p-button-text p-button-plain ${
                        currentPage === totalPages - 1 || totalRecords === 0
                            ? 'cursor-not-allowed'
                            : ''
                    }`}
                    onClick={() => onPageChange(totalPages - 1)}
                    disabled={
                        currentPage === totalPages - 1 || totalRecords === 0
                    }
                />
            </div>
            <div>
                <Dropdown
                    panelClassName="no-padding-drop-item"
                    options={options}
                    value={pageSize.value || 10}
                    onChange={e => onPageSizeChange(e.value)}
                    itemTemplate={item => (
                        <div
                            className="col-12 cursor-pointer px-3 py-2"
                            onMouseEnter={() => {
                                if (onPageRowsHovered) {
                                    onPageRowsHovered(item.value)
                                }
                            }}>
                            {item?.label}
                        </div>
                    )}
                />
            </div>
        </div>
    )
})

export default StyledTablePaginator
